import React from 'react';
import { CaretRight } from '@phosphor-icons/react';

/**
 * One row in a More group: icon, label, an optional quiet line, and a
 * trailing mark. A real button, at least 44px tall.
 *
 * Accepts `onSelect` as well as `onClick` so the settings that were built as
 * dropdown-menu items can render as rows with the handler they already have.
 * forwardRef because dialog triggers attach to it.
 */
const SettingRow = React.forwardRef(({
  icon: Icon, label, hint, trailing, onSelect, onClick, disabled, tone, className = '', ...rest
}, ref) => (
  <button
    ref={ref}
    type="button"
    disabled={disabled}
    onClick={(e) => { if (onSelect) onSelect(e); if (onClick) onClick(e); }}
    className={[
      'flex w-full min-h-[52px] items-center gap-3 px-4 py-2.5 text-left transition-colors',
      'hover:bg-accent/40 disabled:opacity-50 disabled:pointer-events-none',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
      className,
    ].join(' ')}
    {...rest}
  >
    {Icon && (
      <Icon
        className="w-5 h-5 shrink-0"
        style={{ color: tone === 'destructive' ? 'hsl(var(--destructive))' : 'hsl(var(--gold))' }}
        aria-hidden="true"
      />
    )}
    <span className="min-w-0 flex-1">
      <span className={`block text-sm ${tone === 'destructive' ? 'text-destructive' : 'text-foreground'}`}>{label}</span>
      {hint && <span className="block text-xs text-muted-foreground leading-snug">{hint}</span>}
    </span>
    {trailing !== undefined
      ? <span className="shrink-0 text-xs text-muted-foreground">{trailing}</span>
      : <CaretRight className="w-4 h-4 shrink-0 text-muted-foreground" aria-hidden="true" />}
  </button>
));
SettingRow.displayName = 'SettingRow';

/** A titled group of rows on one panel, as the More blueprint asks. */
export const SettingGroup = ({ title, children, testid }) => (
  <section data-testid={testid}>
    <h2 className="mb-2 px-1 text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: 'hsl(var(--gold))' }}>
      {title}
    </h2>
    <div className="overflow-hidden rounded-xl border border-border bg-card/85 backdrop-blur-sm divide-y divide-border">
      {children}
    </div>
  </section>
);

export default SettingRow;
